/**
 * Compute per-preset counts for the sprint-scope JQL quick filters from
 * data already on the SprintDetail row. Returns null for any preset whose
 * count we can't derive locally (e.g. "Mine in this sprint" — we don't
 * know the viewer's Jira accountId).
 *
 * Preferred source: the compliance audit, which carries per-rule violations
 * (and matches Jira label state exactly). Fallback: issues_summary[] is
 * sufficient for the missing-sp rule when compliance hasn't run.
 */

import type { ComplianceAudit, IssueSummary, SprintDetail } from "@/api/types";

export type SprintFilterCounts = Record<string, number | null>;

/** Rules that imply a hygiene label (and therefore appear in "All flags"). */
const SIZED_TYPES = new Set(["Story", "Task", "Bug", "Sub-task"]);

function complianceCounts(audit: ComplianceAudit, issues: IssueSummary[]): SprintFilterCounts {
  const byKey = new Map<string, IssueSummary>();
  for (const i of issues) byKey.set(i.key, i);

  // "Open" mirrors the JQL `statusCategory != Done`: a flagged ticket counts
  // toward the actionable filters only while it's not in a terminal status.
  // Unknown keys (not in issues_summary) are treated as open so we don't
  // silently hide a flag.
  const isOpen = (key: string): boolean => {
    const i = byKey.get(key);
    return i ? !i.is_delivered : true;
  };

  let flaggedOpen = 0;
  let missingSpOpen = 0;
  let missingEndDate = 0;
  let missingEpic = 0;
  let epicMissingCapex = 0;

  for (const a of audit.actions ?? []) {
    if (!a.violations || a.violations.length === 0) continue;
    const open = isOpen(a.key);
    if (open) flaggedOpen += 1;
    for (const v of a.violations) {
      // missing-sp is an actionable rule → scoped to open tickets, matching
      // its Done-excluding JQL. missing-end-date (terminal by design),
      // missing-epic and epic-missing-capex (both span all statuses by
      // design, mirroring the capitalization metric) keep every status.
      if (v.rule_id === "missing-sp") {
        if (open) missingSpOpen += 1;
      } else if (v.rule_id === "missing-epic") {
        missingEpic += 1;
      } else if (v.rule_id === "epic-missing-capex") {
        epicMissingCapex += 1;
      } else if (v.rule_id === "missing-end-date") {
        missingEndDate += 1;
      }
    }
  }

  return {
    "sprint-all":                 flaggedOpen,
    "sprint-mine":                null,
    "sprint-missing-sp":          missingSpOpen,
    "sprint-missing-end-date":    missingEndDate,
    "sprint-missing-epic":        missingEpic,
    "sprint-epic-missing-capex":  epicMissingCapex,
  };
}

function fallbackCounts(issues: IssueSummary[]): SprintFilterCounts {
  // Without a compliance audit we can only confidently match the missing-sp
  // rule: estimable types where SP is unset. The other counts depend on
  // labels (which we don't have on issues_summary) or due_date (which we
  // don't expose). Scoped to still-open tickets to match the Done-excluding
  // `sprint-missing-sp` JQL.
  const missingSp = issues.filter(
    (i) => i.story_points == null && SIZED_TYPES.has(i.type) && !i.is_delivered,
  ).length;
  return {
    "sprint-all":              null,
    "sprint-mine":             null,
    "sprint-missing-sp":          missingSp,
    "sprint-missing-end-date":    null,
    // Epic linkage / capitalization aren't on issues_summary — only the
    // compliance audit knows them.
    "sprint-missing-epic":        null,
    "sprint-epic-missing-capex":  null,
  };
}

export function sprintFilterCounts(sprint: SprintDetail | null | undefined): SprintFilterCounts {
  if (!sprint) return {};
  const issues: IssueSummary[] = sprint.metrics?.issues_summary ?? [];
  const audit = sprint.compliance;
  if (audit && audit.mode !== "off" && (audit.actions?.length ?? 0) > 0) {
    return complianceCounts(audit, issues);
  }
  return fallbackCounts(issues);
}
